import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import * as Colors from 'ui-library/Colors';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {StepObject, StepperStep, StepperStepProps} from 'ui-library/stepper/Stepper';

import {ReplaceTableColumnMapping, ReplaceTableColumnMappingProps} from './ReplaceTableColumnMapping';

export type ColumnMappingStepperStepProps = ReplaceTableColumnMappingProps & {
  newDataFrameState: AsyncStatus;
};

export function ColumnMappingStepperStep(props: ColumnMappingStepperStepProps): JSX.Element {
  // We want to automap columns when the preview columns are first rendered. But if the user updates the
  // preview, ReplaceTableColumnMapping gets unmounted and the remounted, causing the column mapper to  wipe
  // out any user changes. This state variable lets us only call automap the first time the component is mounted.
  let inner;
  if (props.newDataFrameState === AsyncStatus.LOADING) {
    inner = <CenteredHorizontalThrobber />;
  } else if (props.newDataFrameColumns == null) {
    inner = <span className={css(styles.emptyStateText)}>Configure your new table to map columns.</span>;
  } else {
    inner = <ReplaceTableColumnMapping {...props} />;
  }
  return inner;
}

export function getColumnMappingStepperStep(
  params: ColumnMappingStepperStepProps
): React.ReactElement<StepperStepProps> {
  // StepperStep must be returned or the stepper won't render it, but we need to use state to control automapping,
  // so we call a child component here.
  return (
    <StepperStep title="Map columns (optional)">
      <ColumnMappingStepperStep {...params} />
    </StepperStep>
  );
}

export function getColumnMappingStepperStepParams(
  params: ColumnMappingStepperStepProps & {key: string}
): StepObject {
  // StepperStep must be returned or the stepper won't render it, but we need to use state to control automapping,
  // so we call a child component here.
  const {key, ...columnMappingStepperStepProps} = params;
  return {
    title: 'Map columns (optional)',
    content: <ColumnMappingStepperStep {...columnMappingStepperStepProps} />,
    key,
  };
}

const styles = StyleSheet.create({
  emptyStateText: {
    color: Colors.GRAY_DARKER,
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
});
